// Real Davis-Bacon page (replaces the Sprint 1 stub).
//
// Lists all WH-347 weekly certified payrolls for the project. Click a
// week → opens the printable WH-347 form in a new tab (Print → Save as PDF).
// Source: 40 USC 3141 + 29 CFR 5.5.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

const DEMO_PROJECT_ID = '85a46d17-50f3-4bcc-be60-68074efcd5c7';

type Obligation = {
  id: string;
  due_date: string;
  period_start: string | null;
  period_end: string | null;
  status: string;
  notes: string | null;
  code: string;
  name: string;
  cadence_rule: string;
};

const STATUS_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  pending:     { bg: '#f1f5f9', fg: '#475569', label: 'Pending' },
  in_progress: { bg: '#fef3c7', fg: '#92400e', label: 'In progress' },
  submitted:   { bg: '#dbeafe', fg: '#1e40af', label: 'Submitted' },
  accepted:    { bg: '#d1fae5', fg: '#065f46', label: 'Accepted' },
  rejected:    { bg: '#fee2e2', fg: '#991b1b', label: 'Rejected' },
  overdue:     { bg: '#fecaca', fg: '#7f1d1d', label: 'Overdue' },
  na:          { bg: '#e5e7eb', fg: '#6b7280', label: 'N/A' },
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_BADGE[status] ?? STATUS_BADGE.pending;
  return (
    <span style={{
      background: s.bg, color: s.fg,
      padding: '2px 8px', borderRadius: 999,
      fontSize: 10, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '.04em',
    }}>{s.label}</span>
  );
}

function parseLocalDate(input: any): Date | null {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const m = String(input).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
}

const fmtDate = (d: any) => {
  const dt = parseLocalDate(d);
  return dt ? dt.toLocaleDateString() : '—';
};

function KpiCard({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  const color =
    tone === 'good' ? '#10b981' :
    tone === 'warn' ? '#f59e0b' :
    tone === 'bad'  ? '#ef4444' : 'var(--text)';
  return (
    <div className="card">
      <div className="card__body" style={{ padding: 16 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 700, color, marginTop: 4 }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

export default function DavisBaconPage() {
  const [projectId, _setProjectId] = useState(DEMO_PROJECT_ID);

  const { data: obligations = [], isLoading } = useQuery<Obligation[]>({
    queryKey: ['davis-bacon-payrolls', projectId],
    queryFn: () => api.get(`/bead-pm/projects/${projectId}/obligations?status=`),
  });

  // Filter to WH-347s only
  const wh347 = obligations
    .filter(o => o.code === 'FED-LAB-WH347')
    .sort((a, b) => (b.due_date ?? '').localeCompare(a.due_date ?? ''));

  const accepted = wh347.filter(o => o.status === 'accepted').length;
  const inProgress = wh347.filter(o => o.status === 'in_progress').length;
  const pending = wh347.filter(o => o.status === 'pending').length;
  const totalHours = 850; // synthesized total for demo

  return (
    <>
      <div className="page__header">
        <div>
          <h1 className="page__title">Davis-Bacon</h1>
          <div className="page__subtitle">
            Weekly Form WH-347 certified payrolls · 29 CFR 5.5 · Wage determination CO20240003
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <KpiCard label="Crews on Davis-Bacon" value="2" sub="Construction + Splicing crews" />
        <KpiCard label="WH-347 this period" value={`${inProgress + pending}`} sub={`${pending} pending, ${inProgress} in progress`} tone="warn" />
        <KpiCard label="WH-347 accepted YTD" value={`${accepted}`} tone="good" sub={`${totalHours} hrs total`} />
        <KpiCard label="Wage classifications" value="4" sub="Lineman, Splicer, Operator, Laborer" />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card__body" style={{ padding: 14, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ fontSize: 22, lineHeight: 1, padding: '4px 8px', borderRadius: 6, background: 'rgba(245,158,11,0.12)', color: '#d97706' }}>★</div>
          <div style={{ flex: 1, fontSize: 13, color: '#475569', lineHeight: 1.55 }}>
            <b style={{ color: '#0f172a' }}>How this works.</b> Every Friday, your payroll system feeds in. Each row below
            is one week's certified payroll. Click any row to generate the printable Form WH-347 (with Statement of
            Compliance) for signature and submission to the SBO. The form pre-populates with worker classifications,
            hours, gross wages, and deductions from your payroll feed.
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card__header">
          <span>Weekly Certified Payrolls</span>
        </div>
        <div className="card__body" style={{ padding: 0 }}>
          {isLoading ? (
            <div style={{ padding: 16, color: 'var(--text-muted)' }}>Loading payrolls…</div>
          ) : wh347.length === 0 ? (
            <div style={{ padding: 16, color: 'var(--text-muted)' }}>No certified payrolls scheduled.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                  <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>Week ending</th>
                  <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>Period</th>
                  <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>Crews</th>
                  <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', textAlign: 'right' }}>Hours</th>
                  <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>Status</th>
                  <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {wh347.map((o, i) => {
                  // Synthesize realistic demo numbers per row
                  const hours = o.status === 'accepted' ? [186, 198, 152, 78][i % 4] ?? 160 : (o.status === 'in_progress' ? 142 : 0);
                  const crews = hours > 100 ? 2 : (hours > 0 ? 1 : 0);
                  return (
                    <tr key={o.id}
                      onClick={() => window.open(`/bead-compliance/davis-bacon/wh347/${o.id}`, '_blank')}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
                      style={{ borderTop: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.12s' }}>
                      <td style={{ padding: '10px 16px', fontWeight: 600 }}>{fmtDate(o.due_date)}</td>
                      <td style={{ padding: '10px 16px', color: 'var(--text-muted)', fontSize: 12 }}>
                        {fmtDate(o.period_start)} → {fmtDate(o.period_end)}
                      </td>
                      <td style={{ padding: '10px 16px' }}>{crews || '—'}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{hours || '—'}</td>
                      <td style={{ padding: '10px 16px' }}><StatusBadge status={o.status} /></td>
                      <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#3b82f6' }}>Open WH-347 →</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
